import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Code,
  Copy,
  Check,
  ExternalLink,
  Calculator,
  Palette,
  Settings,
  Globe,
  LayoutDashboard,
  Home,
  HelpCircle,
} from "lucide-react";
import { toast } from "sonner";

const WIDGET_URL = process.env.REACT_APP_BACKEND_URL?.replace('/api', '') || window.location.origin;

const EmbedPreview = () => {
  const [copied, setCopied] = useState(false);
  const [widgetWidth, setWidgetWidth] = useState("100%");
  const [widgetHeight, setWidgetHeight] = useState("800");

  const embedCode = `<!-- Sherpa Business Valuation Widget -->
<div id="valuation-widget-container"></div>
<script>
(function() {
  var iframe = document.createElement('iframe');
  iframe.src = '${WIDGET_URL}/widget';
  iframe.style.width = '${widgetWidth}';
  iframe.style.height = '${widgetHeight}px';
  iframe.style.border = 'none';
  iframe.style.borderRadius = '16px';
  iframe.style.boxShadow = '0 25px 50px -12px rgb(0 0 0 / 0.15)';
  iframe.setAttribute('title', 'Business Valuation Calculator');
  iframe.setAttribute('loading', 'lazy');
  document.getElementById('valuation-widget-container').appendChild(iframe);
})();
</script>`;

  const simpleEmbed = `<iframe 
  src="${WIDGET_URL}/widget" 
  width="${widgetWidth}" 
  height="${widgetHeight}" 
  style="border:none; border-radius:16px; box-shadow: 0 25px 50px -12px rgb(0 0 0 / 0.15);"
  title="Business Valuation Calculator"
  loading="lazy">
</iframe>`;

  const copyToClipboard = (code) => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    toast.success("Copied to clipboard!");
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="embed-preview-page">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 h-full w-64 bg-white border-r border-slate-200 z-50 hidden lg:block">
        <div className="p-6">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-10 h-10 bg-emerald-600 rounded-xl flex items-center justify-center">
              <Calculator className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="font-heading font-bold text-slate-900">Valuation</h1>
              <p className="text-xs text-slate-500">Admin Dashboard</p>
            </div>
          </div>

          <nav className="space-y-1">
            <a
              href="/admin"
              className="flex items-center gap-3 px-4 py-3 rounded-xl text-slate-600 hover:bg-slate-50 transition-colors"
            >
              <LayoutDashboard className="w-5 h-5" />
              Dashboard
            </a>
            <a
              href="/"
              className="flex items-center gap-3 px-4 py-3 rounded-xl text-slate-600 hover:bg-slate-50 transition-colors"
            >
              <Home className="w-5 h-5" />
              View Widget
            </a>
            <a
              href="/embed"
              className="flex items-center gap-3 px-4 py-3 rounded-xl bg-emerald-50 text-emerald-700 font-medium"
            >
              <Code className="w-5 h-5" />
              Embed Code
            </a>
          </nav>
        </div>

        <div className="absolute bottom-0 left-0 right-0 p-6 border-t border-slate-100">
          <div className="flex items-center gap-3 text-slate-500 text-sm">
            <HelpCircle className="w-4 h-4" />
            <span>Need help? Contact support</span>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="lg:ml-64 p-6 md:p-8">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-slate-900 mb-2">
              Embed Your Widget
            </h1>
            <p className="text-slate-500">
              Add the business valuation tool to any website with a simple code snippet
            </p>
          </div>

          {/* Customization */}
          <Card className="mb-8 border-slate-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Palette className="w-5 h-5 text-emerald-600" />
                Customize Widget
              </CardTitle>
              <CardDescription>Adjust the dimensions to fit your website layout</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label className="text-slate-700">Width</Label>
                  <Input
                    value={widgetWidth}
                    onChange={(e) => setWidgetWidth(e.target.value)}
                    placeholder="100% or 600px"
                    className="h-10"
                    data-testid="input-widget-width"
                  />
                  <p className="text-xs text-slate-500">Use % for responsive or px for fixed width</p>
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-700">Height (px)</Label>
                  <Input
                    value={widgetHeight}
                    onChange={(e) => setWidgetHeight(e.target.value)}
                    placeholder="800"
                    className="h-10"
                    data-testid="input-widget-height"
                  />
                  <p className="text-xs text-slate-500">Recommended: 700-900px</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Embed Codes */}
          <Card className="mb-8 border-slate-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Code className="w-5 h-5 text-emerald-600" />
                Embed Code
              </CardTitle>
              <CardDescription>Choose the method that works best for your website</CardDescription>
            </CardHeader>
            <CardContent>
              <Tabs defaultValue="script" className="w-full">
                <TabsList className="grid w-full grid-cols-2 mb-4">
                  <TabsTrigger value="script" data-testid="tab-script">Script Tag</TabsTrigger>
                  <TabsTrigger value="iframe" data-testid="tab-iframe">iFrame</TabsTrigger>
                </TabsList>

                <TabsContent value="script">
                  <div className="relative">
                    <pre className="bg-slate-900 text-slate-100 p-4 rounded-xl overflow-x-auto text-sm">
                      <code>{embedCode}</code>
                    </pre>
                    <Button
                      size="sm"
                      className="absolute top-3 right-3 h-8"
                      onClick={() => copyToClipboard(embedCode)}
                      data-testid="btn-copy-script"
                    >
                      {copied ? <Check className="w-4 h-4 mr-1" /> : <Copy className="w-4 h-4 mr-1" />}
                      {copied ? "Copied!" : "Copy"}
                    </Button>
                  </div>
                  <p className="text-sm text-slate-500 mt-3">
                    Recommended for dynamic loading and better performance on slower connections.
                  </p>
                </TabsContent>

                <TabsContent value="iframe">
                  <div className="relative">
                    <pre className="bg-slate-900 text-slate-100 p-4 rounded-xl overflow-x-auto text-sm">
                      <code>{simpleEmbed}</code>
                    </pre>
                    <Button
                      size="sm"
                      className="absolute top-3 right-3 h-8"
                      onClick={() => copyToClipboard(simpleEmbed)}
                      data-testid="btn-copy-iframe"
                    >
                      {copied ? <Check className="w-4 h-4 mr-1" /> : <Copy className="w-4 h-4 mr-1" />}
                      {copied ? "Copied!" : "Copy"}
                    </Button>
                  </div>
                  <p className="text-sm text-slate-500 mt-3">
                    Simple iframe embed. Works on all platforms including Webflow and WordPress.
                  </p>
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>

          {/* Platform Instructions */}
          <Card className="mb-8 border-slate-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Globe className="w-5 h-5 text-emerald-600" />
                Platform-Specific Instructions
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="border-b border-slate-100 pb-4">
                <h3 className="font-semibold text-slate-900 mb-2">WordPress</h3>
                <ol className="text-sm text-slate-600 space-y-1 list-decimal list-inside">
                  <li>Go to the page/post editor</li>
                  <li>Add a "Custom HTML" block</li>
                  <li>Paste the embed code</li>
                  <li>Save and preview</li>
                </ol>
              </div>

              <div className="border-b border-slate-100 pb-4">
                <h3 className="font-semibold text-slate-900 mb-2">Webflow</h3>
                <ol className="text-sm text-slate-600 space-y-1 list-decimal list-inside">
                  <li>Add an "Embed" element to your page</li>
                  <li>Paste the embed code in the HTML field</li>
                  <li>Adjust the embed element size as needed</li>
                  <li>Publish your site</li>
                </ol>
              </div>

              <div className="border-b border-slate-100 pb-4">
                <h3 className="font-semibold text-slate-900 mb-2">Framer</h3>
                <ol className="text-sm text-slate-600 space-y-1 list-decimal list-inside">
                  <li>Add a "Code" component from the Insert menu</li>
                  <li>Select "Embed" as the type</li>
                  <li>Paste the embed code</li>
                  <li>Resize the component</li>
                </ol>
              </div>

              <div>
                <h3 className="font-semibold text-slate-900 mb-2">Static HTML</h3>
                <ol className="text-sm text-slate-600 space-y-1 list-decimal list-inside">
                  <li>Open your HTML file</li>
                  <li>Paste the code where you want the widget</li>
                  <li>Save and upload to your server</li>
                </ol>
              </div>
            </CardContent>
          </Card>

          {/* Live Preview */}
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Settings className="w-5 h-5 text-emerald-600" />
                Live Preview
              </CardTitle>
              <CardDescription>See how the widget will look on your site</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="bg-slate-100 rounded-xl p-4 md:p-8">
                <div
                  className="mx-auto overflow-hidden"
                  style={{
                    width: widgetWidth,
                    maxWidth: "100%",
                    height: `${widgetHeight}px`,
                  }}
                >
                  <iframe
                    src="/widget"
                    style={{
                      width: "100%",
                      height: "100%",
                      border: "none",
                      borderRadius: "16px",
                      boxShadow: "0 25px 50px -12px rgb(0 0 0 / 0.15)",
                    }}
                    title="Widget Preview"
                    data-testid="widget-preview-iframe"
                  />
                </div>
              </div>
              <div className="flex justify-center mt-4">
                <Button
                  variant="outline"
                  onClick={() => window.open("/widget", "_blank")}
                  className="h-10"
                  data-testid="btn-open-fullscreen"
                >
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Open Full Screen
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default EmbedPreview;
